"use strict";
/**
 * Utility functions for generating thumbnails from HTML content
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractPreviewText = exports.generateThumbnailFromHtmlWithIframe = exports.generateThumbnailFromHtml = void 0;
const html2canvas_1 = __importDefault(require("html2canvas"));
const generateThumbnailFromHtml = (htmlContent_1, ...args_1) => __awaiter(void 0, [htmlContent_1, ...args_1], void 0, function* (htmlContent, thumbWidth = 200, thumbHeight = 113) {
    return new Promise((resolve, reject) => {
        try {
            // First, try to find iframe preview (most accurate)
            const iframeSlide = findIframePreview();
            if (iframeSlide) {
                console.log('Found iframe preview, capturing it directly');
                captureExistingSlide(iframeSlide).then(resolve).catch(() => {
                    // Fallback to finding regular slide
                    const existingSlide = findExistingSlide();
                    if (existingSlide) {
                        captureExistingSlide(existingSlide).then(resolve).catch(() => {
                            createAndCaptureSlide(htmlContent).then(resolve).catch(() => {
                                resolve(createEnhancedFallbackThumbnail(htmlContent, thumbWidth, thumbHeight));
                            });
                        });
                    }
                    else {
                        createAndCaptureSlide(htmlContent).then(resolve).catch(() => {
                            resolve(createEnhancedFallbackThumbnail(htmlContent, thumbWidth, thumbHeight));
                        });
                    }
                });
            }
            else {
                // Fallback: try to find regular slide
                const existingSlide = findExistingSlide();
                if (existingSlide) {
                    console.log('Found existing slide in document, capturing it directly');
                    captureExistingSlide(existingSlide).then(resolve).catch(() => {
                        // Fallback to creating a new slide
                        createAndCaptureSlide(htmlContent).then(resolve).catch(() => {
                            resolve(createEnhancedFallbackThumbnail(htmlContent, thumbWidth, thumbHeight));
                        });
                    });
                }
                else {
                    console.log('No existing slide found, creating new slide for capture');
                    createAndCaptureSlide(htmlContent).then(resolve).catch(() => {
                        resolve(createEnhancedFallbackThumbnail(htmlContent, thumbWidth, thumbHeight));
                    });
                }
            }
        }
        catch (error) {
            console.error('Error in thumbnail generation:', error);
            resolve(createEnhancedFallbackThumbnail(htmlContent, thumbWidth, thumbHeight));
        }
    });
});
exports.generateThumbnailFromHtml = generateThumbnailFromHtml;
// Function to find an existing slide in the current document or iframe
const findExistingSlide = () => {
    var _a;
    // First, try to find iframe previews (most accurate)
    const iframes = document.querySelectorAll('iframe');
    for (const iframe of iframes) {
        try {
            const iframeDoc = iframe.contentDocument || ((_a = iframe.contentWindow) === null || _a === void 0 ? void 0 : _a.document);
            if (iframeDoc) {
                // Look for slides inside iframe
                const slideSelectors = [
                    '.presentation-slide',
                    '.slide',
                    '[class*="slide"]',
                    'div[class*="presentation"]',
                    'body' // Fallback to entire iframe body
                ];
                for (const selector of slideSelectors) {
                    const slide = iframeDoc.querySelector(selector);
                    if (slide && slide.offsetWidth > 0 && slide.offsetHeight > 0) {
                        console.log(`Found slide in iframe with selector: ${selector}`);
                        return slide;
                    }
                }
            }
        }
        catch (e) {
            // Cross-origin iframe, skip
            console.log('Skipping cross-origin iframe');
        }
    }
    // Fallback: Look for slides in main document
    const slideSelectors = [
        '.presentation-slide',
        '.slide',
        '[class*="slide"]',
        'div[class*="presentation"]',
        '.preview-container .presentation-slide',
        '.editor-container .presentation-slide',
        '.code-preview .presentation-slide'
    ];
    for (const selector of slideSelectors) {
        const slide = document.querySelector(selector);
        if (slide && slide.offsetWidth > 0 && slide.offsetHeight > 0) {
            console.log(`Found existing slide with selector: ${selector}`);
            return slide;
        }
    }
    return null;
};
// Function to specifically find iframe previews (most accurate)
const findIframePreview = () => {
    var _a;
    console.log('Looking for iframe previews...');
    const iframes = document.querySelectorAll('iframe');
    console.log(`Found ${iframes.length} iframes`);
    for (let i = 0; i < iframes.length; i++) {
        const iframe = iframes[i];
        try {
            const iframeDoc = iframe.contentDocument || ((_a = iframe.contentWindow) === null || _a === void 0 ? void 0 : _a.document);
            if (iframeDoc) {
                console.log(`Checking iframe ${i + 1} for slides...`);
                // Look for slides inside iframe with more specific selectors
                const slideSelectors = [
                    '.presentation-slide',
                    '.slide',
                    '[class*="slide"]',
                    'div[class*="presentation"]',
                    'body > div', // First div in body
                    'body' // Fallback to entire iframe body
                ];
                for (const selector of slideSelectors) {
                    const slide = iframeDoc.querySelector(selector);
                    if (slide && slide.offsetWidth > 0 && slide.offsetHeight > 0) {
                        console.log(`✅ Found slide in iframe ${i + 1} with selector: ${selector}`);
                        console.log(`Slide dimensions: ${slide.offsetWidth}x${slide.offsetHeight}`);
                        // Check if this slide has overlays or UI elements that might interfere
                        const hasOverlays = slide.querySelector('.overlay, .shade, .ui-element, .preview-overlay');
                        if (hasOverlays) {
                            console.log('⚠️ Slide has overlays, trying to find clean version...');
                            // Try to find a cleaner version without overlays
                            const cleanSlide = slide.cloneNode(true);
                            // Remove overlay elements
                            cleanSlide.querySelectorAll('.overlay, .shade, .ui-element, .preview-overlay').forEach(el => el.remove());
                            if (cleanSlide.offsetWidth > 0 && cleanSlide.offsetHeight > 0) {
                                console.log('✅ Found clean slide version');
                                return cleanSlide;
                            }
                        }
                        return slide;
                    }
                }
                // Also check if the iframe itself contains the slide
                if (iframe.offsetWidth > 0 && iframe.offsetHeight > 0) {
                    console.log(`✅ Using iframe ${i + 1} itself as slide`);
                    console.log(`Iframe dimensions: ${iframe.offsetWidth}x${iframe.offsetHeight}`);
                    return iframe;
                }
            }
        }
        catch (e) {
            // Cross-origin iframe, skip
            console.log(`Skipping cross-origin iframe ${i + 1}`);
        }
    }
    console.log('No iframe previews found');
    return null;
};
// Function to capture an existing slide with all its styles - EXACT CAPTURE
const captureExistingSlide = (slide) => __awaiter(void 0, void 0, void 0, function* () {
    console.log('Capturing existing slide with dimensions:', slide.offsetWidth, 'x', slide.offsetHeight);
    // Create a clean clone of the slide to avoid capturing overlays
    const cleanSlide = slide.cloneNode(true);
    // Remove common overlay elements that might interfere with capture
    const overlaySelectors = [
        '.overlay',
        '.shade',
        '.ui-element',
        '.preview-overlay',
        '.editor-overlay',
        '.selection-overlay',
        '.drag-handle',
        '.resize-handle',
        '.hover-overlay',
        '[style*="pointer-events: none"]',
        '[style*="z-index"]'
    ];
    overlaySelectors.forEach(selector => {
        cleanSlide.querySelectorAll(selector).forEach(el => el.remove());
    });
    // Remove any elements with high z-index that might be overlays
    const allElements = cleanSlide.querySelectorAll('*');
    allElements.forEach(el => {
        const computedStyle = window.getComputedStyle(el);
        const zIndex = parseInt(computedStyle.zIndex);
        if (zIndex > 1000) { // Likely an overlay
            el.remove();
        }
    });
    // Wait for any images to load
    yield waitForImages(cleanSlide);
    // Wait for fonts to load
    yield document.fonts.ready;
    // Give a moment for any dynamic content to render
    yield new Promise(resolve => setTimeout(resolve, 200));
    // Capture the clean slide with html2canvas - EXACT dimensions
    const canvas = yield (0, html2canvas_1.default)(cleanSlide, {
        scale: 1, // Use actual scale to get exact image
        backgroundColor: null,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: cleanSlide.offsetWidth,
        height: cleanSlide.offsetHeight,
        x: 0,
        y: 0,
        removeContainer: true, // Remove the temporary container
    });
    // Create thumbnail-sized version to reduce file size
    const thumbWidth = 200;
    const thumbHeight = 113;
    const thumbCanvas = document.createElement('canvas');
    const thumbCtx = thumbCanvas.getContext('2d');
    if (!thumbCtx) {
        throw new Error('Could not get canvas context for thumbnail');
    }
    thumbCanvas.width = thumbWidth;
    thumbCanvas.height = thumbHeight;
    // Draw the captured slide scaled down to thumbnail size
    thumbCtx.drawImage(canvas, 0, 0, thumbWidth, thumbHeight);
    // Get Base64 - THUMBNAIL sized for API with high compression
    const base64 = thumbCanvas.toDataURL('image/jpeg', 0.6); // Use JPEG with higher compression
    console.log('Clean slide captured:', base64.substring(0, 100) + '...');
    console.log('Original canvas dimensions:', canvas.width, 'x', canvas.height);
    console.log('Thumbnail dimensions:', thumbWidth, 'x', thumbHeight);
    console.log('Base64 size (approx):', Math.round(base64.length / 1024), 'KB');
    return base64;
});
// Function to create a new slide and capture it - EXACT CAPTURE
const createAndCaptureSlide = (htmlContent) => __awaiter(void 0, void 0, void 0, function* () {
    // Extract the first slide from the HTML content
    const firstSlide = extractFirstSlide(htmlContent);
    // Create a temporary container that inherits all styles from the current document
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.width = '800px';
    container.style.height = '450px';
    container.style.backgroundColor = 'transparent';
    container.style.overflow = 'visible';
    // Copy all stylesheets from the current document
    copyStylesheets(container);
    // Add the slide content with minimal wrapper
    // The firstSlide now includes any style tags, so we can include it directly
    container.innerHTML = `
    <div class="slide" style="
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px;
      box-sizing: border-box;
    ">
      ${firstSlide}
    </div>
  `;
    // Append to document temporarily
    document.body.appendChild(container);
    // Wait for styles and fonts to apply
    yield new Promise(resolve => setTimeout(resolve, 300));
    yield document.fonts.ready;
    yield waitForImages(container);
    try {
        const slide = container.querySelector('.slide');
        if (!slide) {
            throw new Error('Slide element not found');
        }
        console.log('Capturing new slide with dimensions:', slide.offsetWidth, 'x', slide.offsetHeight);
        // Render the slide into a canvas using html2canvas - EXACT capture
        const canvas = yield (0, html2canvas_1.default)(slide, {
            scale: 1, // Use actual scale to get exact image
            backgroundColor: null,
            useCORS: true,
            allowTaint: true,
            logging: false,
            width: slide.offsetWidth,
            height: slide.offsetHeight,
            x: 0,
            y: 0,
        });
        // Create thumbnail-sized version to reduce file size
        const thumbWidth = 200;
        const thumbHeight = 113;
        const thumbCanvas = document.createElement('canvas');
        const thumbCtx = thumbCanvas.getContext('2d');
        if (!thumbCtx) {
            throw new Error('Could not get canvas context for thumbnail');
        }
        thumbCanvas.width = thumbWidth;
        thumbCanvas.height = thumbHeight;
        // Draw the captured slide scaled down to thumbnail size
        thumbCtx.drawImage(canvas, 0, 0, thumbWidth, thumbHeight);
        // Get Base64 - THUMBNAIL sized for API with high compression
        const base64 = thumbCanvas.toDataURL('image/jpeg', 0.6); // Use JPEG with higher compression
        console.log('Exact new slide captured:', base64.substring(0, 100) + '...');
        console.log('Original canvas dimensions:', canvas.width, 'x', canvas.height);
        console.log('Thumbnail dimensions:', thumbWidth, 'x', thumbHeight);
        console.log('Base64 size (approx):', Math.round(base64.length / 1024), 'KB');
        return base64;
    }
    finally {
        // Ensure cleanup
        if (document.body.contains(container)) {
            document.body.removeChild(container);
        }
    }
});
// Function to copy all stylesheets to the container
const copyStylesheets = (container) => {
    // Copy all link stylesheets
    const linkSheets = document.querySelectorAll('link[rel="stylesheet"]');
    linkSheets.forEach(link => {
        const newLink = link.cloneNode(true);
        container.appendChild(newLink);
    });
    // Copy all style elements
    const styleSheets = document.querySelectorAll('style');
    styleSheets.forEach(style => {
        const newStyle = style.cloneNode(true);
        container.appendChild(newStyle);
    });
    // Copy computed styles from common classes
    const commonClasses = ['MuiBox-root', 'MuiTypography-root', 'presentation-slide'];
    commonClasses.forEach(className => {
        const elements = document.querySelectorAll(`.${className}`);
        if (elements.length > 0) {
            const element = elements[0];
            const computedStyle = window.getComputedStyle(element);
            const styleText = Array.from(computedStyle).map(prop => `${prop}: ${computedStyle.getPropertyValue(prop)}`).join('; ');
            const styleElement = document.createElement('style');
            styleElement.textContent = `.${className} { ${styleText} }`;
            container.appendChild(styleElement);
        }
    });
};
// Function to wait for images to load
const waitForImages = (container) => {
    return new Promise((resolve) => {
        const images = container.querySelectorAll('img');
        if (images.length === 0) {
            resolve();
            return;
        }
        let loadedCount = 0;
        const totalImages = images.length;
        const checkComplete = () => {
            loadedCount++;
            if (loadedCount === totalImages) {
                resolve();
            }
        };
        images.forEach(img => {
            if (img.complete) {
                checkComplete();
            }
            else {
                img.onload = checkComplete;
                img.onerror = checkComplete;
            }
        });
    });
};
// Alternative method using iframe (can be enabled later if html2canvas is available)
const generateThumbnailFromHtmlWithIframe = (htmlContent_1, ...args_1) => __awaiter(void 0, [htmlContent_1, ...args_1], void 0, function* (htmlContent, width = 300, height = 200) {
    return new Promise((resolve, reject) => {
        try {
            // Create a temporary iframe for better HTML rendering
            const iframe = document.createElement('iframe');
            iframe.style.position = 'absolute';
            iframe.style.left = '-9999px';
            iframe.style.top = '-9999px';
            iframe.style.width = `${width}px`;
            iframe.style.height = `${height}px`;
            iframe.style.border = 'none';
            iframe.style.backgroundColor = '#ffffff';
            // Append to document temporarily
            document.body.appendChild(iframe);
            // Wait for iframe to load
            iframe.onload = () => {
                var _a;
                try {
                    const doc = iframe.contentDocument || ((_a = iframe.contentWindow) === null || _a === void 0 ? void 0 : _a.document);
                    if (!doc) {
                        throw new Error('Could not access iframe document');
                    }
                    // Set up the iframe content
                    doc.open();
                    doc.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <style>
                  body {
                    margin: 0;
                    padding: 10px;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    font-size: 14px;
                    line-height: 1.4;
                    color: #333;
                    background: #ffffff;
                    overflow: hidden;
                    width: ${width - 20}px;
                    height: ${height - 20}px;
                  }
                  .presentation-slide {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    text-align: center;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    padding: 20px;
                    box-sizing: border-box;
                  }
                  h1, h2, h3, h4, h5, h6 {
                    margin: 0 0 10px 0;
                    font-weight: 600;
                  }
                  p {
                    margin: 5px 0;
                  }
                  img {
                    max-width: 100%;
                    max-height: 60%;
                    object-fit: contain;
                  }
                </style>
              </head>
              <body>
                <div class="presentation-slide">
                  ${htmlContent}
                </div>
              </body>
            </html>
          `);
                    doc.close();
                    // Wait a bit for content to render
                    setTimeout(() => {
                        try {
                            // Try to use html2canvas if available
                            if (window.html2canvas) {
                                window.html2canvas(doc.body, {
                                    width,
                                    height,
                                    scale: 1,
                                    useCORS: true,
                                    allowTaint: true,
                                    backgroundColor: '#ffffff',
                                }).then((canvas) => {
                                    const dataUrl = canvas.toDataURL('image/png');
                                    document.body.removeChild(iframe);
                                    resolve(dataUrl);
                                }).catch((error) => {
                                    console.warn('html2canvas failed, using fallback:', error);
                                    document.body.removeChild(iframe);
                                    resolve(createEnhancedFallbackThumbnail(htmlContent, width, height));
                                });
                            }
                            else {
                                // Use enhanced fallback
                                document.body.removeChild(iframe);
                                resolve(createEnhancedFallbackThumbnail(htmlContent, width, height));
                            }
                        }
                        catch (error) {
                            console.error('Error in thumbnail generation:', error);
                            document.body.removeChild(iframe);
                            resolve(createEnhancedFallbackThumbnail(htmlContent, width, height));
                        }
                    }, 200);
                }
                catch (error) {
                    console.error('Error setting up iframe content:', error);
                    document.body.removeChild(iframe);
                    resolve(createEnhancedFallbackThumbnail(htmlContent, width, height));
                }
            };
            // Set iframe source to trigger load
            iframe.src = 'about:blank';
        }
        catch (error) {
            console.error('Error creating iframe:', error);
            resolve(createEnhancedFallbackThumbnail(htmlContent, width, height));
        }
    });
});
exports.generateThumbnailFromHtmlWithIframe = generateThumbnailFromHtmlWithIframe;
// Helper function to extract the first slide from HTML content
const extractFirstSlide = (htmlContent) => {
    // Try to find the first slide element
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    // First, extract any style tags that might contain slide-specific CSS
    const styleTags = tempDiv.querySelectorAll('style');
    let styleContent = '';
    styleTags.forEach(style => {
        styleContent += style.outerHTML;
    });
    console.log('Found style tags:', styleTags.length);
    if (styleContent) {
        console.log('Style content:', styleContent.substring(0, 200) + '...');
    }
    // Look for common slide selectors
    const slideSelectors = [
        '.presentation-slide',
        '.slide',
        '[class*="slide"]',
        'div[class*="presentation"]',
        'section'
    ];
    for (const selector of slideSelectors) {
        const slide = tempDiv.querySelector(selector);
        if (slide) {
            console.log(`Found slide with selector: ${selector}`);
            console.log('Slide content:', slide.outerHTML.substring(0, 200) + '...');
            // Return both style tags and the slide content
            const result = styleContent + slide.outerHTML;
            console.log('Combined result length:', result.length);
            return result;
        }
    }
    console.log('No slide found, wrapping entire content');
    // If no slide found, wrap the entire content in a slide container
    return styleContent + `<div class="presentation-slide">${htmlContent}</div>`;
};
// Helper function to resize canvas to thumbnail size while maintaining aspect ratio
const resizeCanvasToThumbnail = (sourceCanvas, targetWidth, targetHeight) => {
    // Calculate the target dimensions maintaining 16:9 aspect ratio
    const aspectRatio = 16 / 9;
    let finalWidth = targetWidth;
    let finalHeight = Math.round(finalWidth / aspectRatio);
    // If the calculated height exceeds target height, adjust width instead
    if (finalHeight > targetHeight) {
        finalHeight = targetHeight;
        finalWidth = Math.round(finalHeight * aspectRatio);
    }
    // Create new canvas for thumbnail
    const thumbnailCanvas = document.createElement('canvas');
    const thumbnailCtx = thumbnailCanvas.getContext('2d');
    if (!thumbnailCtx) {
        return sourceCanvas.toDataURL('image/png');
    }
    thumbnailCanvas.width = finalWidth;
    thumbnailCanvas.height = finalHeight;
    // Draw the source canvas scaled to thumbnail size
    thumbnailCtx.drawImage(sourceCanvas, 0, 0, sourceCanvas.width, sourceCanvas.height, 0, 0, finalWidth, finalHeight);
    return thumbnailCanvas.toDataURL('image/png');
};
// Helper function to manually render slide content when html2canvas is not available
const renderSlideManually = (ctx, slideHtml, width, height) => {
    // Parse the slide HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = slideHtml;
    // Create gradient background
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, '#f8f9fa');
    gradient.addColorStop(1, '#ffffff');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    // Add border
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 4;
    ctx.strokeRect(2, 2, width - 4, height - 4);
    // Add inner border
    ctx.strokeStyle = '#dee2e6';
    ctx.lineWidth = 2;
    ctx.strokeRect(20, 20, width - 40, height - 40);
    // Extract and render content
    const textContent = tempDiv.textContent || tempDiv.innerText || 'Slide Content';
    const hasImages = tempDiv.querySelectorAll('img').length > 0;
    const hasHeadings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6').length > 0;
    // Content area
    const contentArea = {
        x: 40,
        y: 40,
        width: width - 80,
        height: height - 80
    };
    // Draw content area background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(contentArea.x, contentArea.y, contentArea.width, contentArea.height);
    // Draw image placeholder if images exist
    if (hasImages) {
        ctx.fillStyle = '#e3f2fd';
        ctx.fillRect(contentArea.x + 20, contentArea.y + 20, contentArea.width - 40, 120);
        // Draw image icon
        ctx.fillStyle = '#1976d2';
        ctx.font = 'bold 48px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('🖼️', contentArea.x + contentArea.width / 2, contentArea.y + 95);
    }
    // Draw text content
    ctx.fillStyle = '#2c3e50';
    ctx.font = hasHeadings ? 'bold 32px Arial' : '24px Arial';
    ctx.textAlign = 'center';
    // Truncate text
    const maxLength = hasImages ? 40 : 60;
    const displayText = textContent.length > maxLength
        ? textContent.substring(0, maxLength) + '...'
        : textContent;
    const words = displayText.split(' ');
    const lineHeight = hasHeadings ? 40 : 32;
    const maxWidth = contentArea.width - 40;
    let y = hasImages ? contentArea.y + 160 : contentArea.y + contentArea.height / 2 - (words.length * lineHeight) / 2;
    let line = '';
    let linesDrawn = 0;
    const maxLines = hasImages ? 3 : 5;
    for (let i = 0; i < words.length && linesDrawn < maxLines; i++) {
        const testLine = line + words[i] + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && i > 0) {
            ctx.fillText(line, contentArea.x + contentArea.width / 2, y);
            line = words[i] + ' ';
            y += lineHeight;
            linesDrawn++;
        }
        else {
            line = testLine;
        }
    }
    // Draw remaining text
    if (line.trim() && linesDrawn < maxLines) {
        ctx.fillText(line, contentArea.x + contentArea.width / 2, y);
    }
};
const createEnhancedFallbackThumbnail = (htmlContent, width, height) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return '';
    }
    canvas.width = width;
    canvas.height = height;
    // Create gradient background
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, '#ffffff');
    gradient.addColorStop(1, '#f8f9fa');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    // Add border
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, width - 2, height - 2);
    // Add inner border for slide effect
    ctx.strokeStyle = '#dee2e6';
    ctx.lineWidth = 1;
    ctx.strokeRect(10, 10, width - 20, height - 20);
    // Extract and analyze content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || 'Template Preview';
    const hasImages = tempDiv.querySelectorAll('img').length > 0;
    const hasHeadings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6').length > 0;
    // Draw slide content area
    const contentArea = {
        x: 15,
        y: 15,
        width: width - 30,
        height: height - 30
    };
    // Add content area background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(contentArea.x, contentArea.y, contentArea.width, contentArea.height);
    // Draw content based on what we found
    if (hasImages) {
        // Draw image placeholder
        ctx.fillStyle = '#e3f2fd';
        ctx.fillRect(contentArea.x + 20, contentArea.y + 20, contentArea.width - 40, 60);
        // Draw image icon
        ctx.fillStyle = '#1976d2';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('🖼️', contentArea.x + contentArea.width / 2, contentArea.y + 55);
    }
    // Draw text content
    ctx.fillStyle = '#333333';
    ctx.font = hasHeadings ? 'bold 16px Arial' : '14px Arial';
    ctx.textAlign = 'center';
    // Truncate and wrap text
    const maxLength = hasImages ? 30 : 40;
    const displayText = textContent.length > maxLength
        ? textContent.substring(0, maxLength) + '...'
        : textContent;
    const words = displayText.split(' ');
    const lineHeight = hasHeadings ? 20 : 18;
    const maxWidth = contentArea.width - 20;
    let y = hasImages ? contentArea.y + 90 : contentArea.y + contentArea.height / 2 - (words.length * lineHeight) / 2;
    let line = '';
    let linesDrawn = 0;
    const maxLines = hasImages ? 2 : 4;
    for (let i = 0; i < words.length && linesDrawn < maxLines; i++) {
        const testLine = line + words[i] + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && i > 0) {
            ctx.fillText(line, contentArea.x + contentArea.width / 2, y);
            line = words[i] + ' ';
            y += lineHeight;
            linesDrawn++;
        }
        else {
            line = testLine;
        }
    }
    // Draw remaining text
    if (line.trim() && linesDrawn < maxLines) {
        ctx.fillText(line, contentArea.x + contentArea.width / 2, y);
    }
    // Add template indicator
    ctx.fillStyle = '#28a745';
    ctx.font = 'bold 12px Arial';
    ctx.fillText('TEMPLATE', width - 60, height - 10);
    // Add document type indicator
    ctx.fillStyle = '#6c757d';
    ctx.font = '10px Arial';
    ctx.fillText('PPT', 10, height - 10);
    return canvas.toDataURL('image/png');
};
/**
 * Extract a preview text from HTML content
 */
const extractPreviewText = (htmlContent, maxLength = 100) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    return textContent.length > maxLength
        ? textContent.substring(0, maxLength) + '...'
        : textContent;
};
exports.extractPreviewText = extractPreviewText;
